import OpenInNew from "@mui/icons-material/OpenInNew";
import Preview from "@mui/icons-material/Preview";
import { IconButton, Stack, Tooltip, Button } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  StaffPipelinesTableQuery,
  StaffPipelinesTableQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import { STAFF_PIPELINES_TABLE_QUERY } from "./api";
import { DispatchWastePipelineRunDialog } from "./DispatchWastePipelineRunDialog";
import { PipelineDialog } from "./PipelineDialog";

const REVIEW_FILE_SUFFIX = "waste_manifest_review.json";

function hasReviewableOutput(outputFiles: string[]): boolean {
  return outputFiles.some((f) => f.endsWith(REVIEW_FILE_SUFFIX));
}

type PipelineRow = StaffPipelinesTableQuery["pipelines"]["items"][number];

export const Pipelines = () => {
  useBreadcrumb({ label: "Pipelines" });

  const { selectedId: pipelineId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("pipelineId");
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);

  const columns: OmnisearchGridColDef<PipelineRow>[] = useMemo(
    () => [
      {
        headerName: "Organization",
        field: "tenant",
        flex: 1.2,
        sortable: false,
        filterKeyType: "multipleTenant",
        valueGetter(_, row) {
          return row.tenant?.name ?? "";
        },
      },
      {
        headerName: "Pipeline Name",
        field: "name",
        flex: 1.2,
      },
      {
        headerName: "Last Run",
        field: "lastRunAt",
        filterKeyType: "time",
        flex: 1,
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "Never";
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1.2,
        filterKeyType: "time",
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        flex: 1.2,
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        filterKeyType: "time",
        flex: 1.2,
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        type: "actions",
        field: "actions",
        getActions: ({ row }) => {
          const actions = [];
          if (hasReviewableOutput(row.outputFiles)) {
            actions.push(
              <Tooltip key="results" title="View Results">
                <IconButton
                  component={Link}
                  to={`./${row.id}/results`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Preview />
                </IconButton>
              </Tooltip>
            );
          }
          actions.push(
            <Tooltip key="open" title="Open in new tab">
              <IconButton
                component={Link}
                to={`./${row.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
          );
          return actions;
        },
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<StaffPipelinesTableQuery, StaffPipelinesTableQueryVariables, PipelineRow>
        persistenceKey="staffPipelines"
        isURLDriven
        rowHeight="auto"
        initialSortModel={[{ field: "updatedAt", sort: "desc" }]}
        onRowClick={(params) => {
          pushIdToUrl(params.row.id);
        }}
        columns={columns}
        dataQuery={STAFF_PIPELINES_TABLE_QUERY}
        getItems={(data) => data.pipelines.items}
        getCount={(data) => data.pipelines.count}
        commandButtons={[
          <Button
            key="dispatch-waste-pipeline"
            variant="contained"
            color="secondary"
            onClick={() => setShowDispatchDialog(true)}
          >
            New Waste Pipeline Run
          </Button>,
        ]}
      />
      {showDispatchDialog && (
        <DispatchWastePipelineRunDialog onClose={() => setShowDispatchDialog(false)} />
      )}
      {!!pipelineId && (
        <PipelineDialog
          pipelineId={pipelineId}
          onClose={() => {
            popIdFromUrl();
          }}
        />
      )}
    </Stack>
  );
};
